import {
  Client,
  CurrencyType,
  GROUP_PT_CURRENCY_TYPES,
  PT_CURRENCY_TYPES
} from '../models/trainer.models';

const FLEXIBLE_TYPES: CurrencyType[] = [
  CurrencyType.PersonalTrainingFlexible,
  CurrencyType.GroupTrainingFlexible
];

const STRICT_TYPES: CurrencyType[] = [
  CurrencyType.PersonalTrainingStrict,
  CurrencyType.GroupTrainingStrict,
  CurrencyType.FitnessAssessment
];

function isStrictCurrencyType(t: CurrencyType): boolean {
  return STRICT_TYPES.includes(t);
}

export function formatSessionCountForDisplay(sessions: number): string {
  if (Math.abs(sessions - Math.round(sessions)) < 1e-9) {
    return Math.round(sessions).toString();
  }
  return sessions.toFixed(2);
}

/** Only shorter Flexible booking. 15- and 45-minute sessions are exact scales, not splits. */
const FLEXIBLE_SPLIT_MINUTES = 30;

/**
 * Pooled Flexible minutes usable for a `durationMinutes` booking. Mirrors Facility
 * `PTCurrencyBookingCandidates`: same scale, a 30-minute split of a longer scale, or a
 * shorter scale that divides the duration. A Flexible 60 pack does not fund 15 or 45.
 */
export function isAssessmentCurrencyType(type: CurrencyType | undefined): boolean {
  return type === CurrencyType.FitnessAssessment;
}

function rowMatchesSlot(
  b: { Type: CurrencyType; CurrencyName?: string },
  isGroup: boolean,
  isAssessment: boolean
): boolean {
  if (isAssessment) {
    return b.Type === CurrencyType.FitnessAssessment;
  }
  if (isGroup) {
    return GROUP_PT_CURRENCY_TYPES.includes(b.Type);
  }
  return PT_CURRENCY_TYPES.includes(b.Type);
}

function flexiblePoolMinutes(client: Client, durationMinutes: number, isGroup: boolean, isAssessment = false): number {
  const balances = client.CurrencyBalances;
  if (!balances?.length) {
    return 0;
  }
  let total = 0;
  for (const b of balances) {
    if (!rowMatchesSlot(b, isGroup, isAssessment)) {
      continue;
    }
    if (!FLEXIBLE_TYPES.includes(b.Type)) {
      continue;
    }
    const scale = Number(b.Scale);
    const usable =
      !(scale > 0) ||
      scale === durationMinutes ||
      (durationMinutes === FLEXIBLE_SPLIT_MINUTES && scale > FLEXIBLE_SPLIT_MINUTES) ||
      (scale > 0 && scale < durationMinutes && durationMinutes % scale === 0);
    if (!usable) {
      continue;
    }
    const bal = Number(b.Balance);
    if (!Number.isFinite(bal)) {
      continue;
    }
    total += bal;
  }
  return total;
}

function strictSessionsAtDuration(client: Client, durationMinutes: number, isGroup: boolean, isAssessment = false): number {
  const balances = client.CurrencyBalances;
  if (!balances?.length || durationMinutes <= 0) {
    return 0;
  }
  let sessions = 0;
  for (const b of balances) {
    if (!rowMatchesSlot(b, isGroup, isAssessment)) {
      continue;
    }
    if (!isStrictCurrencyType(b.Type)) {
      continue;
    }
    const scale = Number(b.Scale);
    if (scale !== durationMinutes) {
      continue;
    }
    const bal = Number(b.Balance);
    if (!Number.isFinite(bal)) {
      continue;
    }
    const unit = (b.ProductUnit ?? '').toLowerCase();
    if (unit === 'session' || unit === 'sessions') {
      sessions += bal;
    } else {
      sessions += scale > 0 ? Math.floor(bal / scale) : 0;
    }
  }
  return sessions;
}

/** Bookable session count at `durationMinutes` (eligible Flexible pool ÷ duration + Strict lines at this scale). */
export function sessionCountForBookingSlot(
  client: Client,
  durationMinutes: number,
  isGroup: boolean,
  isAssessment = false
): number {
  const balances = client.CurrencyBalances;
  if (!balances?.length) {
    if (isAssessment) {
      return 0;
    }
    const legacyRaw = isGroup ? client.GroupSessions : client.PTSessions;
    const legacy = Number(legacyRaw);
    if (!Number.isFinite(legacy) || legacy < 0) {
      return 0;
    }
    return durationMinutes > 0 ? legacy / durationMinutes : 0;
  }
  const flexMinutes = flexiblePoolMinutes(client, durationMinutes, isGroup, isAssessment);
  const flexCount = durationMinutes > 0 ? flexMinutes / durationMinutes : 0;
  const strictCount = strictSessionsAtDuration(client, durationMinutes, isGroup, isAssessment);
  return flexCount + strictCount;
}

function hasFlexibleCurrencyForBookingCategory(client: Client, isGroup: boolean): boolean {
  const balances = client.CurrencyBalances;
  if (!balances?.length) {
    return false;
  }
  const categoryTypes = isGroup ? GROUP_PT_CURRENCY_TYPES : PT_CURRENCY_TYPES;
  for (const b of balances) {
    if (!categoryTypes.includes(b.Type)) {
      continue;
    }
    if (FLEXIBLE_TYPES.includes(b.Type)) {
      return true;
    }
  }
  return false;
}

/**
 * Balance lines for a booking (matches df-frontdesk MemberSessionBalanceLine):
 * Strict-only → selected duration; Flexible → selected duration first, then 60 and 30 min.
 */
export function getBalanceDisplayLinesForBooking(
  client: Client,
  duration: number,
  isGroup: boolean,
  isAssessment = false
): string[] {
  const prefix = isAssessment ? 'Assessment' : isGroup ? 'Group' : 'Private';
  const noun = (c: number) => (Math.abs(c - 1) < 1e-9 ? 'session' : 'sessions');
  const durations = !isAssessment && hasFlexibleCurrencyForBookingCategory(client, isGroup)
    ? [duration, ...[60, 30].filter((d) => d !== duration)]
    : [duration];
  return durations.map((d) => {
    const count = sessionCountForBookingSlot(client, d, isGroup, isAssessment);
    return `${prefix} (${d} min) : ${formatSessionCountForDisplay(count)} ${noun(count)}`;
  });
}

/** Private + Group summaries at 60 min for roster / directory views. */
export function rosterCreditSummaryLines(client: Client): string[] {
  return [
    ...getBalanceDisplayLinesForBooking(client, 60, false),
    ...getBalanceDisplayLinesForBooking(client, 60, true)
  ];
}

/** Structured counts for roster UI (Private/Group × 60/45/30 min). */
export interface RosterBalanceStat {
  id: string;
  title: string;
  category: string;
  duration: string;
  value: string;
  scope: 'private' | 'group';
}

export function rosterBalanceStats(client: Client): RosterBalanceStat[] {
  const cells: Array<{
    id: string;
    isGroup: boolean;
    duration: number;
    category: string;
    durationLabel: string;
  }> = [
    { id: 'p60', isGroup: false, duration: 60, category: 'Private', durationLabel: '60 min' },
    { id: 'p45', isGroup: false, duration: 45, category: 'Private', durationLabel: '45 min' },
    { id: 'p30', isGroup: false, duration: 30, category: 'Private', durationLabel: '30 min' },
    { id: 'g60', isGroup: true, duration: 60, category: 'Group', durationLabel: '60 min' },
    { id: 'g45', isGroup: true, duration: 45, category: 'Group', durationLabel: '45 min' },
    { id: 'g30', isGroup: true, duration: 30, category: 'Group', durationLabel: '30 min' }
  ];
  return cells.map((c) => ({
    id: c.id,
    title: `${c.category} sessions bookable at ${c.durationLabel}`,
    category: c.category,
    duration: c.durationLabel,
    value: formatSessionCountForDisplay(sessionCountForBookingSlot(client, c.duration, c.isGroup)),
    scope: c.isGroup ? 'group' : 'private'
  }));
}
